import React, { useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { scrollIntoCanvas } from "@/lib/scroll";

/** Section list for the customer document; `tone` says whether the section is settled, needs attention, or is empty. */
export function tocSections({ vm, texts = {}, customerView }) {
  const txt = vm?.texts || {};
  const t = vm?.totals || {};
  const text = (key, label) => {
    const r = texts[key] || {};
    const has = Boolean(txt[key]);
    if (!has && customerView) return null;
    const tone = !has ? "empty" : r.blocks_control || r.needs_review ? "warn" : "ok";
    return { key, label: r.label || label, tone };
  };
  return [
    text("folgebrev", "Følgebrev"),
    text("omfang", "Omfang"),
    { key: "priser", label: "Leveranse og priser", tone: t.preliminary ? "warn" : "ok" },
    vm?.options?.length ? { key: "opsjoner", label: "Opsjoner", tone: "ok" } : null,
    vm?.assumptions?.kunde?.length || vm?.assumptions?.los?.length ? { key: "forutsetninger-poster", label: "Forutsetninger per post", tone: "ok" } : null,
    text("forbehold", "Forbehold"),
    text("forutsetninger", "Forutsetninger"),
    text("vilkar", "Vilkår"),
    text("gyldighet", "Gyldighet"),
    text("avslutning", "Avslutning"),
  ].filter(Boolean);
}

export const TOC_DOT = { warn: "bg-[hsl(var(--v-assumed))]", empty: "border border-black/[0.25] bg-transparent" };

/** Scrolls the paper to one section – only the canvas moves, never the shell. */
export function scrollToSection(key, paperId = "tilbud-paper", offset = 112) {
  scrollIntoCanvas(document.querySelector(`[data-testid="${paperId}"] [data-section="${key}"]`), { offset });
}

/**
 * Quiet section list beside the paper. Only deviations get a mark: amber when the section needs attention, hollow when it
 * is still empty (Rediger). Click scrolls the paper; the active section follows the scroll.
 */
export function QuoteToc({ sections, paperId = "tilbud-paper", className, offset = 112 }) {
  const [active, setActive] = useState(sections[0]?.key);
  const lockUntil = useRef(0);
  const keys = useMemo(() => sections.map((s) => s.key).join("|"), [sections]);
  useEffect(() => {
    const paper = document.querySelector(`[data-testid="${paperId}"]`);
    const root = paper?.closest('[data-testid="project-canvas-scroll"]') || null;
    if (!paper || typeof IntersectionObserver === "undefined") return undefined;
    const els = Array.from(paper.querySelectorAll("[data-section]"));
    const io = new IntersectionObserver((entries) => {
      if (Date.now() < lockUntil.current) return;
      const hit = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
      if (hit) setActive(hit.target.getAttribute("data-section"));
    }, { root, rootMargin: "-120px 0px -60% 0px", threshold: 0 });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [paperId, keys]);
  const go = (key) => {
    lockUntil.current = Date.now() + 900; // the clicked section stays active while the smooth scroll runs
    scrollToSection(key, paperId, offset);
    setActive(key);
  };
  if (!sections.length) return null;
  return (
    <nav className={cn("text-[12.5px]", className)} aria-label="Innhold i tilbudet" data-testid="tilbud-toc">
      <ol className="space-y-px">
        {sections.map((s) => (
          <li key={s.key}>
            <button type="button" onClick={() => go(s.key)} aria-current={active === s.key ? "true" : undefined} className={cn("flex w-full items-center gap-2 rounded-[8px] px-2 py-1.5 text-left transition-colors hover:bg-black/[0.04] hover:text-foreground", active === s.key ? "font-medium text-foreground" : "text-muted-foreground")} data-testid={`tilbud-toc-${s.key}`} data-tone={s.tone}>
              <span className="truncate">{s.label}</span>
              {TOC_DOT[s.tone] ? <span className={cn("toc-dot ml-auto", TOC_DOT[s.tone])} aria-hidden /> : null}
            </button>
          </li>
        ))}
      </ol>
    </nav>
  );
}
